import LicenseBuilderModel from "../../../common/models/tracked/licenseBuilderModel";
import LicenseStep from "./licensing/licenseStep";
import LicenseStepFactory from "./licensing/licenseStepFactory";
import MaintenanceStep from "./licensing/maintenanceStep";
import RegistrationStep from "./licensing/registrationStep";
import RenewalStep from "./licensing/renewalStep";
import LicenseResource from "./licensing/licenseResource";
import IRootBuilderModel from "../../../common/interfaces/builderModelInterfaces";
import { AttributeMetadataInterface } from "../../../common/interfaces/attributeMetadataInterface";
import TimeOffset from "../../../common/models/timeOffset";
import PrerequisiteLicense from "./licensing/prerequisiteLicense";
import Validatable from "../../../common/mixins/validatable";
import LicenseBuilderValidation from "../../../common/models/navigableValidations/licenseBuilderValidation";
import Capable from "../../../common/mixins/capable";
import { ICapable } from "../../../common/interfaces/capableInterfaces";
import Organization from "../../../common/models/organization";
import { RelationType } from "../../../common/models/relationType";
import store from "../../../common/state/store";
import IRepo from "../../../common/interfaces/repo";
import Repo from "../../../common/state/modules/repo";
import Shareable from "../../../common/mixins/shareable";
import Share from "../../../common/models/share";
import LicenseNotification from "./licensing/licenseNotification";
import LicenseStatus from "./licenseStatus";
import LicenseStatusAction from "./licensing/licenseStatusAction";
import LicenseLinkedForm from "./licensing/licenseLinkedForm";
import LicenseRelationship from "../../licenseRelationships/models/licenseRelationship";
import { LicenseType } from "../enums/licenseTypes";
import { ApplicationType } from "../enums/applicationType";
import { DocumentOwnerType } from "../../../common/enums/documentOwnerType";
import { IPeekerOptions } from "../../../common/services/peeker";
import CustomGeneratedIdSettings from "~/models/customGeneratedIdSettings";
import ErrorHandler from "~/models/errorHandling/errorHandler";
import {DocumentType} from "~/enums/documentType";

export default class License
  extends Shareable(Capable(Validatable(LicenseBuilderModel)))
  implements IRootBuilderModel, ICapable {
  title: string;
  license = this;
  organization: Organization;
  application_type: ApplicationType;
  license_type: LicenseType;
  license_owner_type: DocumentOwnerType;
  registration_steps: { [key: string]: RegistrationStep } = {};
  maintenance_steps: { [key: string]: MaintenanceStep } = {};
  prerequisite_licenses: { [key: string]: PrerequisiteLicense } = {};
  renewal_steps: { [key: string]: RenewalStep } = {};
  license_notifications: { [key: string]: LicenseNotification } = {};
  resources: { [key: string]: LicenseResource } = {};
  license_statuses: { [key: string]: LicenseStatus } = {};
  notice_start: TimeOffset = new TimeOffset(this);
  status_actions: { [key: string]: LicenseStatusAction } = {};
  linked_forms: { [key: string]: LicenseLinkedForm } = {};
  linked_license_relationships: { [key: string]: LicenseRelationship };
  custom_generated_id_settings: CustomGeneratedIdSettings;
  is_published: boolean;
  has_maintenance_steps: boolean;
  validity_description: string;
  justApproved: boolean;
  forms: any[];
  initial_application_form_id: string;
  initial_application_form: any;
  attributes_metadata: { [key: string]: AttributeMetadataInterface };
  cost_type: boolean;
  min_cost: string;
  max_cost: string;
  certificate: any;
  certificate_id: string;
  apply_custom_text_enabled: boolean;
  apply_section_header: string;
  apply_sub_text: string;
  apply_button_label: string;

  static repo: IRepo<License> = new Repo<License>("licenses", License);

  except = [
    "license",
    "justApproved",
    "prerequisite_licenses",
    "registration_steps",
    "maintenance_steps",
    "linked_license_relationships",
    "renewal_steps",
    "resources",
    "attributes_metadata",
    "organization",
    "shares",
    "license_notifications",
    "initial_application_form",
    "custom_generated_id_settings",
  ];
  attempted_to_publish: boolean;
  edit_document_path: string;

  //******************** VALIDATABLE ********************//
  _validation: LicenseBuilderValidation = new LicenseBuilderValidation(this);

  //******************** VALIDATABLE END ********************//

  static get displayName(): string {
    return "License";
  }

  get UNTRACKED_PROPERTIES(): string[] {
    return [
      "_snapshots",
      "snapshots",
      "u_at",
      "is_published",
      "attributes_metadata",
      "request",
      "initial_application_form",
      "linked_license_relationships"
    ];
  }

  get EMBEDDED_MODELS(): {
    [key: string]: { type: any; relationType: RelationType };
  } {
    return {
      prerequisite_licenses: {
        type: PrerequisiteLicense,
        relationType: RelationType.dictionary
      },
      license_statuses: {
        type: LicenseStatus,
        relationType: RelationType.dictionary
      },
      status_actions: {
        type: LicenseStatusAction,
        relationType: RelationType.dictionary
      },
      linked_forms: {
        type: LicenseLinkedForm,
        relationType: RelationType.dictionary
      },
      registration_steps: {
        type: LicenseStep,
        relationType: RelationType.dictionary
      },
      maintenance_steps: {
        type: LicenseStep,
        relationType: RelationType.dictionary
      },
      renewal_steps: {
        type: LicenseStep,
        relationType: RelationType.dictionary
      },
      resources: {
        type: LicenseResource,
        relationType: RelationType.dictionary
      },
      license_notifications: {
        type: LicenseNotification,
        relationType: RelationType.dictionary
      },
      notice_start: {
        type: TimeOffset,
        relationType: RelationType.singleObject
      },
      shares: {
        type: Share,
        relationType: RelationType.collection
      },
      custom_generated_id_settings: {
        type: CustomGeneratedIdSettings,
        relationType: RelationType.singleObject
      },
    };
  }

  createEmbeddedObject = {
    registration_steps: (parent: License, json): LicenseStep => {
      return new RegistrationStep(parent);
    },
    maintenance_steps: (parent: License, json): LicenseStep => {
      return new MaintenanceStep(parent);
    },
    prerequisite_licenses: (parent: License, json): PrerequisiteLicense => {
      return new PrerequisiteLicense(parent);
    },
    renewal_steps: (parent: License, json): LicenseStep => {
      return new RenewalStep(parent);
    },
    resources: (parent: License, json): LicenseResource => {
      return new LicenseResource(parent);
    },
    license_notifications: (parent: License, json): LicenseNotification => {
      return new LicenseNotification(parent);
    },
    license_statuses: (parent: License, json): LicenseStatus => {
      return new LicenseStatus().loadFromJson(json, parent);
    },
    status_actions: (parent: License, json): LicenseStatusAction => {
      return new LicenseStatusAction(parent, json);
    },
    linked_forms: (parent: License, json): LicenseLinkedForm => {
      return new LicenseLinkedForm(parent, json);
    },
    notice_start: (parent: License, json): TimeOffset => {
      return new TimeOffset(parent);
    },
    custom_generated_id_settings: (parent: License, json): CustomGeneratedIdSettings => {
      return new CustomGeneratedIdSettings(parent);
    },
  };

  get rootName() {
    return "license";
  }

  get displayName() {
    return "License";
  }

  get collectionKey() {
    return "licenses";
  }

  get classKey(): string {
    return "license";
  }

  get docType(): DocumentType {
    return DocumentType.license;
  }

  createStep(stepType): LicenseStep {
    const step = LicenseStepFactory.create(this, stepType);
    this.placeInDictionary(step);
    this.trackCreationDeletion(step.client_id, null, step.asJson(), step);
    this.saveChangesToStore();
    return step;
  }

  createResource(): LicenseResource {
    const resource = new LicenseResource(this);
    this.placeInDictionary(resource);
    this.trackCreationDeletion(
      resource.client_id,
      null,
      resource.asJson(),
      resource
    );
    this.saveChangesToStore();
    return resource;
  }

  createPrerequisite(): PrerequisiteLicense {
    const prereq = new PrerequisiteLicense(this);
    this.placeInDictionary(prereq);
    this.trackCreationDeletion(prereq.client_id, null, prereq.asJson(), prereq);
    this.saveChangesToStore();
    return prereq;
  }

  createLicenseNotification(): LicenseNotification {
    const licenseNotification = new LicenseNotification(this);
    this.placeInDictionary(licenseNotification);
    this.trackCreationDeletion(
      licenseNotification.client_id,
      null,
      licenseNotification.asJson(),
      licenseNotification
    );
    this.saveChangesToStore();
    return licenseNotification;
  }

  createLinkedForm(): LicenseLinkedForm {
    const linkedForm = new LicenseLinkedForm(this);
    this.trackCreationDeletion(
      linkedForm.client_id,
      null,
      linkedForm.asJson(),
      linkedForm
    );
    return linkedForm;
  }

  createStatusAction(): LicenseStatusAction {
    const statusAction = new LicenseStatusAction(this);
    this.trackCreationDeletion(
      statusAction.client_id,
      null,
      statusAction.asJson(),
      statusAction
    );
    return statusAction;
  }

  get sortedRegistrationSteps(): RegistrationStep[] {
    return this.sortProperty("registration_steps");
  }

  get sortedPrerequisites(): PrerequisiteLicense[] {
    return this.sortProperty("prerequisite_licenses");
  }

  get sortedLinkedForms(): LicenseLinkedForm[] {
    return this.sortProperty("linked_forms");
  }

  get sortedStatusActions(): LicenseStatusAction[] {
    return this.sortProperty("status_actions");
  }

  get sortedMaintenanceSteps(): MaintenanceStep[] {
    return this.sortProperty("maintenance_steps");
  }

  get sortedRenewalSteps(): RenewalStep[] {
    return this.sortProperty("renewal_steps");
  }

  get sortedResources(): LicenseResource[] {
    return this.sortProperty("resources");
  }

  get sortedStatusTypes(): any[] {
    return this.sortProperty("license_statuses");
  }

  get sortedLicenseNotifications(): LicenseNotification[] {
    return this.sortProperty("license_notifications");
  }

  // ******************** TRACK CHANGES******************** //

  get hasChanged() {
    return this._hasChanged;
  }

  set hasChanged(newVal: boolean) {
    if (newVal) {
      this.queueSendChangesToServer(
        this.lastPeekerOptions || {
          area: this.currentBuilderArea(),
          modelId: this.clientIdOrId
        }
      );
      // we call validate for the side effects of recalculating validationResults and errorCount
      if (this.attempted_to_publish) {
        this.validate();
      }
    }
    this._hasChanged = newVal;
  }

  saveToServer(): Promise<any> {
    return store.dispatch("licenseStore/saveToServer").then(res => {
      this.handleErrorsAndUpdateSaveMessage(res.better_errors)
    }).catch(err => {
      ErrorHandler.autosaveCatch(this, err);
    });
  }

  protected saveChanges(
    changes: any,
    peekerOptions: string | IPeekerOptions = {
      area: this.currentBuilderArea(),
      modelId: this.clientIdOrId
    }
  ): void {
    store.commit("licenseStore/saveChanges", { changes, peekerOptions });
  }

  // ******************** TRACK CHANGES END ******************** //

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      case "forms":
        return value.title;
      case "status_change_period_start":
        return "Period Start";
      case "status_change_period_end":
        return "Period End";
      case "generated_at":
        return "Generated At";
      case "generation_period_end":
        return "Generation Period End";
      default:
        return this.attributes_metadata[propName].display_name;
    }
  }
}
